import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, Calendar, ArrowRight } from 'lucide-react';

const Resources = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  
  const categories = ['All', 'Trademark Basics', 'Registration Process', 'Legal Tips', 'Copyright', 'Patents', 'Business Strategy'];
  
  const articles = [
    {
      category: 'Trademark Basics',
      title: 'What is a Trademark and Why Do You Need One?',
      desc: 'Learn the fundamentals of trademark protection and discover why registering your trademark is crucial for brand security.',
      readTime: '5 min read',
      date: 'January 15, 2026'
    },
    {
      category: 'Registration Process',
      title: 'Step-by-Step Guide to Trademark Registration',
      desc: 'A comprehensive walkthrough of the trademark registration process, from initial search to final approval.',
      readTime: '8 min read',
      date: 'January 10, 2026'
    },
    {
      category: 'Legal Tips',
      title: 'Common Trademark Mistakes to Avoid',
      desc: 'Discover the most common pitfalls in trademark registration and how to avoid costly errors.',
      readTime: '6 min read',
      date: 'January 5, 2026'
    },
    {
      category: 'Copyright',
      title: 'Understanding Copyright Protection for Creative Works',
      desc: 'Everything you need to know about protecting your original creative content and artistic works.',
      readTime: '7 min read',
      date: 'December 28, 2024'
    },
    {
      category: 'Patents',
      title: 'Patent vs Trademark: Which Protection Does Your Business Need?',
      desc: 'Understand the difference between patents and trademarks to choose the right IP protection strategy.',
      readTime: '9 min read',
      date: 'December 20, 2024'
    },
    {
      category: 'Business Strategy',
      title: 'Building a Strong Brand Identity Through IP Protection',
      desc: 'Strategic insights on leveraging intellectual property rights to strengthen your brand and business value.',
      readTime: '10 min read',
      date: 'December 15, 2024'
    }
  ];

  const filteredArticles = activeFilter === 'All' 
    ? articles 
    : articles.filter(article => article.category === activeFilter);

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <Helmet>
        <title>Trademark Resources & Guides - Essential IP Protection Information</title>
        <meta name="description" content="Free trademark guides, tips, and resources. Learn about IP protection, registration process, legal advice from expert attorneys." />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-[#F1F1F1] mb-6">Resources & Guides</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Expert insights, practical guides, and the latest updates on intellectual property protection
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-6 py-2 rounded-full transition-all ${
                  activeFilter === category
                    ? 'bg-[#FFB400] text-[#2B0A12] hover:bg-[#e5a200]'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
                <div className="h-48 bg-gradient-to-br from-[#2B0A12] to-[#D72638] flex items-center justify-center">
                  <div className="text-center text-white p-6">
                    <span className="inline-block px-4 py-1 bg-[#FFB400] text-[#2B0A12] rounded-full text-sm font-semibold mb-4">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#2B0A12] mb-3 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{article.desc}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{article.readTime}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{article.date}</span>
                    </div>
                  </div>
                  <Button className="w-full bg-[#2B0A12] hover:bg-[#3D1C24] text-white">
                    Read More <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
